import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { inventoryHierarchyService } from '../../services/inventoryHierarchy.service';

const TreeNode = ({ node, level = 0, expandedNodes, onToggleExpand }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.includes(node.node_id);
  
  const handleToggle = (e) => {
    e.stopPropagation();
    onToggleExpand(node.node_id);
  };
  
  const getNodeIcon = () => {
    if (!hasChildren) return <InventoryIcon fontSize="small" />;
    return isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />;
  };
  
  return (
    <Box sx={{ ml: level * 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {hasChildren && (
          <IconButton size="small" onClick={handleToggle}>
            {getNodeIcon()}
          </IconButton>
        )}
        {!hasChildren && <Box sx={{ ml: 3 }} />}
        <Box sx={{ ml: 1 }}>
          <Typography variant="body1">{node.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {node.sku_count} SKUs • ${node.total_value?.toLocaleString() || '0'}
          </Typography>
        </Box>
      </Box>
      {hasChildren && isExpanded && (
        <Box>
          {node.children.map((child) => (
            <TreeNode
              key={child.node_id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const InventoryHierarchyTree = ({ hierarchy }) => {
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const handleToggleExpand = (nodeId) => {
    setExpandedNodes((prev) =>
      prev.includes(nodeId)
        ? prev.filter((id) => id !== nodeId)
        : [...prev, nodeId]
    );
  };
  
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };
  
  if (!hierarchy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Ensure hierarchy is an array
  const hierarchyArray = Array.isArray(hierarchy) ? hierarchy : [hierarchy];
  
  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search inventory hierarchy..."
        value={searchTerm}
        onChange={handleSearch}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {hierarchyArray.map((node) => (
        <TreeNode
          key={node.node_id}
          node={node}
          expandedNodes={expandedNodes}
          onToggleExpand={handleToggleExpand}
        />
      ))}
    </Box>
  );
};

export default InventoryHierarchyTree; 